import { useState, useEffect, useRef, useMemo } from 'react'
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr'
import type { PrayerRequest, Comment, Discussion, DiscussionComment, ReactionGroup } from '@/types'

export interface GroupHubHandlers {
  onPrayerRequestCreated?: (prayerRequest: PrayerRequest) => void
  onPrayerRequestUpdated?: (prayerRequest: PrayerRequest) => void
  onPrayerRequestDeleted?: (prayerRequestId: string) => void
  onCommentAdded?: (data: { prayerRequestId: string; comment: Comment }) => void
  onCommentUpdated?: (data: { prayerRequestId: string; comment: Comment }) => void
  onCommentDeleted?: (data: { prayerRequestId: string; commentId: string }) => void
  onDiscussionCreated?: (discussion: Discussion) => void
  onDiscussionUpdated?: (discussion: Discussion) => void
  onDiscussionDeleted?: (discussionId: string) => void
  onDiscussionCommentAdded?: (data: { discussionId: string; comment: DiscussionComment }) => void
  onMemberJoined?: (data: { groupId: string; displayName: string }) => void
  onMemberLeft?: (data: { groupId: string; displayName: string }) => void
  onReactionToggled?: (data: { entityId: string; entityType: string; reactions: ReactionGroup[] }) => void
}

export function useGroupHub(groupIds: string[], handlers: GroupHubHandlers) {
  const handlersRef = useRef(handlers)
  const [retryCount, setRetryCount] = useState(0)
  const unmountedRef = useRef(false)

  useEffect(() => {
    handlersRef.current = handlers
  })

  const groupIdKey = useMemo(() => groupIds.join(','), [groupIds])

  useEffect(() => {
    if (!groupIdKey) return
    unmountedRef.current = false

    const ids = groupIdKey.split(',')

    const connection = new HubConnectionBuilder()
      .withUrl((__API_BASE_URL__ || '') + '/hubs/prayer-group', { withCredentials: true })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .build()

    connection.on('PrayerRequestCreated', (pr) => handlersRef.current.onPrayerRequestCreated?.(pr))
    connection.on('PrayerRequestUpdated', (pr) => handlersRef.current.onPrayerRequestUpdated?.(pr))
    connection.on('PrayerRequestDeleted', (id) => handlersRef.current.onPrayerRequestDeleted?.(id))
    connection.on('CommentAdded', (data) => handlersRef.current.onCommentAdded?.(data))
    connection.on('CommentUpdated', (data) => handlersRef.current.onCommentUpdated?.(data))
    connection.on('CommentDeleted', (data) => handlersRef.current.onCommentDeleted?.(data))
    connection.on('DiscussionCreated', (disc) => handlersRef.current.onDiscussionCreated?.(disc))
    connection.on('DiscussionUpdated', (disc) => handlersRef.current.onDiscussionUpdated?.(disc))
    connection.on('DiscussionDeleted', (id) => handlersRef.current.onDiscussionDeleted?.(id))
    connection.on('DiscussionCommentAdded', (data) => handlersRef.current.onDiscussionCommentAdded?.(data))
    connection.on('MemberJoined', (data) => handlersRef.current.onMemberJoined?.(data))
    connection.on('MemberLeft', (data) => handlersRef.current.onMemberLeft?.(data))
    connection.on('ReactionToggled', (data) => handlersRef.current.onReactionToggled?.(data))

    const joinGroups = async () => {
      for (const groupId of ids) {
        try {
          await connection.invoke('JoinGroup', groupId)
        } catch (err) {
          console.error(`Failed to join group ${groupId}:`, err)
        }
      }
    }

    connection.onreconnected(async () => {
      await joinGroups()
    })

    // When the connection permanently closes (all auto-reconnect attempts exhausted),
    // schedule a fresh connection attempt. This handles mobile scenarios where the OS
    // kills WebSocket connections during sleep.
    connection.onclose(() => {
      if (!unmountedRef.current) {
        const delay = Math.min(5000 * 2 ** retryCount, 60000)
        setTimeout(() => {
          if (!unmountedRef.current) {
            setRetryCount(c => c + 1)
          }
        }, delay)
      }
    })

    connection.start()
      .then(joinGroups)
      .catch(err => console.error('SignalR connection failed:', err))

    return () => {
      unmountedRef.current = true
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop()
      }
    }
  }, [groupIdKey, retryCount])
}
